import { linkifyText } from './linkUtils';

// Enhanced data cleaning function to remove unwanted characters
const cleanUnwantedCharacters = (value: string): string => {
  return value
    .replace(/[�]/g, '') // Remove ugly replacement character
    .replace(/[•]/g, '') // Remove bullet points
    .replace(/[.]{2,}/g, '') // Remove multiple dots
    .replace(/[\[\]"'`]/g, '') // Remove brackets and quotes
    .replace(/[;{}<>=]/g, '') // Remove special characters
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();
};

// Enhanced buying intent cleaning - ONLY for table display
const cleanBuyingIntentValue = (value: any, useTableFormat: boolean = true): string => {
  if (!value || value === 'N/A' || value === '') return 'N/A';
  
  let cleanedValue = String(value);
  
  // Handle different data formats for buying intent
  if (cleanedValue.includes('•') || cleanedValue.includes('-') || cleanedValue.includes('*')) {
    // Already formatted with bullets - clean them
    cleanedValue = cleanedValue.replace(/[•\-*]/g, '').trim();
  }
  
  // Handle array-like data or comma-separated values
  let items: string[] = [];
  
  // Try to parse as JSON array first
  try {
    const parsed = JSON.parse(cleanedValue);
    if (Array.isArray(parsed)) {
      items = parsed;
    }
  } catch {
    // Not JSON, treat as comma/semicolon separated
    items = cleanedValue.split(/[,;|&+\n]/).map(item => cleanUnwantedCharacters(item)).filter(item => item.length > 0);
  }
  
  if (items.length === 0) {
    return 'N/A';
  }
  
  // ONLY use pipe separator for table display, keep commas for everything else
  const separator = useTableFormat ? ' | ' : ', ';
  return items.map(item => cleanUnwantedCharacters(item)).join(separator);
};

export const cleanDisplayValue = (value: any, columnName: string, useTableFormat: boolean = true): React.ReactNode => {
  if (!value || value === 'N/A' || value === '') return 'N/A';
  
  // Convert value to string
  let strValue = String(value);
  
  // Check if the column name suggests it might contain links
  const columnLower = columnName.toLowerCase();
  if (columnLower.includes('email') || 
      columnLower.includes('phone') || 
      columnLower.includes('linkedin') || 
      columnLower.includes('facebook') ||
      columnLower.includes('twitter') ||
      columnLower.includes('website') || 
      columnLower.includes('url') ||
      columnLower.includes('social')) {
    return linkifyText(strValue, { color: 'primary' });
  }

  // Special handling for buying intent columns (including new naming convention)
  const isBuyingIntentColumn = columnLower.includes('buying intent') || 
                                columnLower.includes('data intent') ||
                                columnLower === 'last 7 days' ||
                                columnLower === 'last 30 days' ||
                                columnLower === 'last 90 days' ||
                                columnLower === 'last 60 days';
  if (isBuyingIntentColumn) {
    return cleanBuyingIntentValue(value, useTableFormat);
  }

  // Handle arrays/lists represented as strings
  if (strValue.includes(',') && (
    columnLower.includes('keyword') ||
    columnLower.includes('intent') ||
    columnLower.includes('interest') ||
    columnLower.includes('industry') ||
    columnLower.includes('topic') ||
    columnLower.includes('tag') ||
    columnLower.includes('category')
  )) {
    strValue = cleanUnwantedCharacters(strValue);
    const items = strValue.split(',')
      .map(item => cleanUnwantedCharacters(item))
      .filter(item => item.length > 0);
    // ONLY use pipe separator for table display, keep commas for everything else
    const separator = useTableFormat ? ' | ' : ', ';
    return items.join(separator);
  }

  // Clean other string values
  if (typeof value === 'string') {
    strValue = cleanUnwantedCharacters(strValue);
  }

  return strValue;
};
